import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Business as IceIcon,
  LocationOn as LocationIcon,
  CalendarToday as DateIcon,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { FactureAvoir, FactureAvoirStatus } from '../../types/factureAvoir';

interface FactureAvoirPreviewProps {
  avoir: FactureAvoir;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onPrint?: () => void;
}

const FactureAvoirPreview: React.FC<FactureAvoirPreviewProps> = ({
  avoir,
  open,
  onClose,
  onEdit,
  onPrint,
}) => {
  const getStatusChip = (status: string) => {
    switch (status) {
      case FactureAvoirStatus.BROUILLON:
        return <Chip label="Brouillon" color="default" size="small" />;
      case FactureAvoirStatus.VALIDEE:
        return <Chip label="Validée" color="success" size="small" />;
      case FactureAvoirStatus.ANNULEE:
        return <Chip label="Annulée" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // Get the bon retour number (from linked or manual)
  const getBonRetourNumber = () => {
    if (avoir.bon_retour_number) return avoir.bon_retour_number;
    if (avoir.bon_retour_manual) return avoir.bon_retour_manual;
    return 'N/A';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Avoir {avoir.reference}
            </Typography>
            {getStatusChip(avoir.status)}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Header Info */}
        <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
          Informations Générales
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <DateIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              <Typography variant="body2">
                <strong>Date:</strong> {new Date(avoir.date).toLocaleDateString('fr-FR')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <ReceiptIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              <Typography variant="body2">
                <strong>Facture de Base:</strong> {avoir.facture_reference || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <AssignmentIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              <Typography variant="body2">
                <strong>Bon de Retour:</strong> {getBonRetourNumber()}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Client Info Box */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'rgba(76, 175, 80, 0.08)',
            borderRadius: 1,
            border: '1px solid rgba(76, 175, 80, 0.3)',
          }}
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
            {/* Client Info */}
            <Box flex={1}>
              <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
                Informations du Client
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                  <Typography variant="body2">
                    <strong>{avoir.client_name || 'N/A'}</strong>
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <IceIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                  <Typography variant="body2" color="text.secondary">
                    ICE: {avoir.client_ice || 'N/A'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                  <Typography variant="body2" color="text.secondary">
                    {avoir.client_address || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Vertical Divider */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: 'rgba(76, 175, 80, 0.3)' }} />
            <Divider sx={{ display: { xs: 'block', md: 'none' }, borderColor: 'rgba(76, 175, 80, 0.3)' }} />

            {/* Return Info */}
            <Box flex={1}>
              <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
                Informations de Retour
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="body2">
                  <strong>Motif:</strong> {avoir.motif_retour || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Remarque:</strong> {avoir.remarque_retour || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Items Table */}
        <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
          Articles
        </Typography>
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                <TableCell sx={{ fontWeight: 600 }}>Désignation</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Quantité</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Prix Unitaire</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {avoir.items && avoir.items.length > 0 ? (
                avoir.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.designation}</TableCell>
                    <TableCell align="right">{Number(item.quantite)}</TableCell>
                    <TableCell align="right">
                      {Number(item.prix_unitaire).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </TableCell>
                    <TableCell align="right">
                      {Number(item.total).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Aucun article
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals */}
        <Box display="flex" justifyContent="flex-end">
          <Box sx={{ width: 300, backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Total HT:</Typography>
              <Typography variant="body2">
                {Number(avoir.total_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">TVA ({Number(avoir.tva_rate)}%):</Typography>
              <Typography variant="body2">
                {Number(avoir.tva_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={600}>Total Net:</Typography>
              <Typography variant="subtitle2" fontWeight={600} color="#4CAF50">
                {Number(avoir.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fermer
        </Button>
        {onPrint && (
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={onPrint}
            sx={{ borderColor: '#4CAF50', color: '#4CAF50' }}
          >
            Imprimer
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={onEdit}
          sx={{
            backgroundColor: '#4CAF50',
            '&:hover': { backgroundColor: '#388E3C' },
          }}
        >
          Modifier
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FactureAvoirPreview;
